import { type FC } from 'react';
import classNames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import { FollowButton } from '../../sharedComponents/FollowButton/FollowButton';
import { locations } from '../../sharedComponents/FollowButton/followSearchTracking';
import { DesignSeriesDescription } from '../DesignSeriesDescription/DesignSeriesDescription';
import { DesignSeriesCreatorCollection } from './DesignSeriesCreatorCollection';

import { type DesignSeriesCreator_iconicDesignSeriesPage$data } from './__generated__/DesignSeriesCreator_iconicDesignSeriesPage.graphql';
import { type DesignSeriesCreator_viewer$data } from './__generated__/DesignSeriesCreator_viewer.graphql';

import styles from './DesignSeriesCreator.scss';

type Props = {
    iconicDesignSeriesPage: DesignSeriesCreator_iconicDesignSeriesPage$data;
    viewer: DesignSeriesCreator_viewer$data;
};

const DesignSeriesCreatorComponent: FC<Props> = props => {
    const { viewer, iconicDesignSeriesPage } = props;

    const { displayName, cmsTopModule, linkData } = iconicDesignSeriesPage.primaryCreator || {};
    const { path: creatorPath } = linkData || {};
    const { mainImage, description } = cmsTopModule || {};
    const nullDescriptionClass = {
        [styles.nullDescription]: !description,
    };

    return (
        <div className={classNames(styles.container, nullDescriptionClass)}>
            {description && mainImage && (
                <div className={classNames(styles.creatorCmsInfo, nullDescriptionClass)}>
                    <div className={styles.titleAndImageContainer}>
                        <div className={styles.imageContainer}>
                            <img alt="" className={styles.image} src={mainImage} />
                        </div>
                        <div className={styles.titleContainer}>
                            <div
                                className={styles.creatorName}
                                data-tn="design-series-creator-name"
                            >
                                {displayName}
                            </div>
                            {creatorPath && displayName && (
                                <div className={styles.followButton}>
                                    <FollowButton
                                        viewer={viewer}
                                        followSearchUriRef={creatorPath}
                                        followSearchDisplayName={displayName}
                                        location={locations.DESIGN_SERIES}
                                        gaLabel="iconic design series page"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {description && (
                        <DesignSeriesDescription
                            collapsedHeight={120}
                            descriptionHtml={description}
                            wrapperClass={styles.descriptionContainer}
                        />
                    )}
                </div>
            )}

            <DesignSeriesCreatorCollection iconicDesignSeriesPage={iconicDesignSeriesPage} />
        </div>
    );
};

export const DesignSeriesCreator = createFragmentContainer(DesignSeriesCreatorComponent, {
    viewer: graphql`
        fragment DesignSeriesCreator_viewer on Viewer {
            ...FollowButton_viewer
        }
    `,
    iconicDesignSeriesPage: graphql`
        fragment DesignSeriesCreator_iconicDesignSeriesPage on IconicDesignSeriesPage {
            primaryCreator(maxDesignsCount: $primaryCreatorDesignsMaxCount) {
                displayName
                cmsTopModule {
                    description
                    mainImage
                }
                linkData {
                    path
                }
            }
            ...DesignSeriesCreatorCollection_iconicDesignSeriesPage
        }
    `,
});
